import React, {useCallback, useState} from "react";
import {useSiren} from "../SirenContext";
import {useImportFileSelect} from "../hooks/useImportFileSelect";
import useContactImportParser from "../hooks/useContactImportParser";
import {useMapImportRows} from "../hooks/useMapImportRows";
import {useSubmitImport} from "../hooks/useSubmitImport";
import {useImportProgress} from "../hooks/useImportProgress";
import {ProgressBar} from "../../../features/management-v2/import/ProgressBar";
import BasicText from "../../../components/form-v2/basic-text";
import {ToggleButton, IconButton} from "../../../features/management-v2/import/inputs";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import {useDownloadXLSX} from "../../../features/management-v2/import/useDownloadXLSX";
import DownloadIcon from "@mui/icons-material/Download";

// ---- Step 1: File selection ------------------------------------------------

function SelectFile({onSubmitted}: {onSubmitted: (importID: string) => void}) {
    const {clientID} = useSiren();
    const fileSelect = useImportFileSelect();
    const {headers, rows} = useContactImportParser(fileSelect.selectedFile);
    const contacts = useMapImportRows(headers, rows);
    const {submit, submitting} = useSubmitImport();

    const [name, setName] = useState("");
    const [matchOnEmail, setMatchOnEmail] = useState(false);
    const [archiveMissing, setArchiveMissing] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const {selectedFile, fileInputRef, handleFileChange, activateFileSelect, invalidFile} = fileSelect;

    const {generateAndSave: downloadSample} = useDownloadXLSX(
        "import-contacts-sample.xlsx",
        ["First Name*", "Last Name*", "Email Address", "Personal Email Address", "Work Mobile Phone", "Personal Mobile Phone", "Home Phone", "Group", "Group"],
        [["Jane", "Doe", "jane@example.com", "", "+447700900001", "", "", "Team A", "Team B"]],
    );

    const handleSubmit = useCallback(async () => {
        if (!selectedFile || !name || !clientID) return;
        setError(null);

        const importID = await submit({
            name,
            clientID,
            flags: {matchOnEmail, archiveMissing},
            contacts,
        });
        if (!importID) {
            setError("Failed to submit import.");
            return;
        }

        onSubmitted(importID);
    }, [selectedFile, name, clientID, matchOnEmail, archiveMissing, contacts, submit, onSubmitted]);

    return (
        <div className="bg-white shadow-md p-4 flex flex-col gap-4">
            <h1 className="text-lg font-semibold">Import CSV or XLSX File</h1>
            <IconButton onClick={downloadSample} text="Download Sample" icon={<DownloadIcon />} />

            <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx"
                className="hidden"
                onChange={handleFileChange}
            />
            <IconButton
                onClick={activateFileSelect}
                text={selectedFile ? selectedFile.name : "Select File"}
                icon={<UploadFileIcon />}
                color={invalidFile ? "red" : "blue"}
            />

            {selectedFile && !invalidFile && (
                <>
                    <BasicText value={name} onChange={setName} placeholder="Import Name" />
                    <ToggleButton text="Match Contacts On Email" initialValue={false} onChange={setMatchOnEmail} />
                    <ToggleButton text="Archive Missing" initialValue={false} onChange={setArchiveMissing} />
                    {error && <p className="text-red text-sm">{error}</p>}
                    <IconButton
                        onClick={handleSubmit}
                        color="green"
                        disabled={name.length === 0 || submitting}
                        loading={submitting}
                        text={submitting ? "Submitting…" : `Start Import (${contacts.length} contacts)`}
                        icon={<NavigateNextIcon />}
                    />
                </>
            )}
        </div>
    );
}

// ---- Step 2: Progress view -------------------------------------------------

function ImportProgress({importID}: {importID: string}) {
    const {status, processed, total, counts, errors, connected} = useImportProgress(importID);

    const isDone = status === "complete" || status === "error";

    const statusStyles: Record<string, string> = {
        complete:   "bg-desaturated-green text-dark-green",
        error:      "bg-light-red text-red",
        processing: "bg-light-blue text-dark-blue",
        queued:     "bg-desaturated-grey text-grey",
    };
    const statusCls = statusStyles[status] ?? "bg-desaturated-grey text-grey";

    return (
        <div className="bg-white shadow-md rounded-md p-6 flex flex-col gap-5">
            <div className="flex items-center justify-between">
                <h1 className="text-lg font-semibold">Import Progress</h1>
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full capitalize ${statusCls}`}>
                    {status}{!connected && !isDone ? " · connecting…" : ""}
                </span>
            </div>

            {!isDone && total > 0 && (
                <ProgressBar current={processed} total={total} />
            )}

            <div className="grid grid-cols-5 gap-3">
                {([
                    {label: "Created",  value: counts.created,  color: counts.created  > 0 ? "text-dark-green" : ""},
                    {label: "Updated",  value: counts.updated,  color: ""},
                    {label: "Archived", value: counts.archived, color: ""},
                    {label: "Skipped",  value: counts.skipped,  color: ""},
                    {label: "Failed",   value: counts.failed,   color: counts.failed   > 0 ? "text-red" : ""},
                ] as {label: string; value: number; color: string}[]).map(({label, value, color}) => (
                    <div key={label} className="flex flex-col items-center gap-1 border border-light-grey rounded-md py-3">
                        <span className={`text-xl font-semibold ${color}`}>{value}</span>
                        <span className="text-xs text-grey">{label}</span>
                    </div>
                ))}
            </div>

            {errors.length > 0 && (
                <div className="flex flex-col gap-2">
                    <h3 className="text-sm font-semibold">Row Errors</h3>
                    <div className="max-h-48 overflow-y-auto flex flex-col gap-1.5">
                        {errors.map((e, i) => (
                            <div key={i} className="text-xs bg-light-red border border-mid-red rounded px-3 py-2">
                                <span className="font-semibold text-red">Row {e.row}:</span>
                                <span className="text-dark-dark-grey ml-1">{e.message}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

// ---- Main export -----------------------------------------------------------

export function NewImport() {
    const [importID, setImportID] = useState<string | null>(null);

    if (importID) {
        return <ImportProgress importID={importID} />;
    }

    return <SelectFile onSubmitted={setImportID} />;
}
